// A club's details as Studio saves them: identity, squads and sponsors.
// The venue service stores this; Stage, Pitchside and Matchday setup read it.
import { RULES } from './rules';

export interface Player { id: string; name: string; number: string; photo: string; showPhoto?: boolean }
export interface Squad { label: string; players: Player[] }
export interface Sponsor { name: string; logo: string; bg: string }
export interface Opponent { name: string; short: string; color: string; crest: string | null }
/** A promo video for the screen; stored in pieces by the venue service. */
export interface PromoVideo { id: string; title: string; url: string; durationMs: number | null; bytes: number }
export interface Club {
  id: string; name: string; teamName: string; short: string; color: string; crest: string;
  screen: { width: number; height: number };
  squads: Record<string, Squad>;
  sponsors: Sponsor[];
  league?: Opponent[];        // the clubs in our league, for the opponent picker
  videos?: PromoVideo[];
  /** The rules the club plays: 'rugby-union' (missing = rugby union, as for every club before football) or 'football'. */
  sport?: string;
  /** The Matchlight credit, added by the venue service when the club is read. Studio never saves it. */
  credit?: Credit;
}

/**
 * "Scoreboard by Matchlight" in the sponsor rotation. On for every club unless the owner (Tidemark code)
 * switches it off; club admins can't see or change it. Kept apart from the club's details for that reason.
 */
export interface Credit { on: boolean; tagline: string }
export const DEFAULT_TAGLINE = 'Live scoreboards for club sport, run from a phone';
export const DEFAULT_CREDIT: Credit = { on: true, tagline: DEFAULT_TAGLINE };
export function cleanCredit(x: unknown): Credit {
  const o = (x ?? {}) as Record<string, unknown>;
  return { on: o.on !== false, tagline: str(o.tagline, 60) || DEFAULT_TAGLINE };
}

/** Made-up clubs for sales calls and demos (rugby, football). Matchday setup can put them back to how they started. */
export const DEMO_CLUBS = new Set(['kestrel-vale', 'copperbeck-town']);

export const LIMITS = { squads: 20, players: 80, sponsors: 40, opponents: 200, league: 60, videos: 12, mediaBytes: 1_500_000, videoBytes: 30_000_000 };

const COMMON_FIRST = new Set(['west', 'east', 'north', 'south', 'old', 'new', 'upper', 'lower', 'st', 'saint', 'great', 'little']);
/** A three-letter scoreboard name: "Doncaster Phoenix" → DON, "West Leeds" → WLE, "Old Crossleyans" → OCR. */
export function shortName(name: string): string {
  const words = name.replace(/[^A-Za-z\s]/g, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return 'TBC';
  const s = words.length > 1 && COMMON_FIRST.has(words[0].toLowerCase()) ? words[0][0] + words[1].slice(0, 2) : words[0].slice(0, 3);
  return s.toUpperCase().padEnd(3, words[1]?.[0]?.toUpperCase() ?? 'X').slice(0, 3);
}

const str = (v: unknown, max: number) => String(v ?? '').replace(/[\u0000-\u001f]/g, '').trim().slice(0, max);
const color = (v: unknown, d = '#888888') => (/^#[0-9a-f]{6}$/i.test(String(v)) ? String(v).toLowerCase() : d);
/** Images may only point at the club's own files or uploaded media. */
export const assetPath = (v: unknown) => (typeof v === 'string' && /^\/(clubs|media)\/[\w\-./]+$/.test(v) && !v.includes('..') ? v : '');
const slug = (v: unknown, d: string) => (str(v, 24).toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '') || d);

export class ClubError extends Error {}

/** Clean whatever Studio sends into a safe Club, or throw a ClubError the organiser can act on. */
export function sanitizeClub(input: unknown, venue: string): Club {
  const x = (input ?? {}) as Record<string, any>;
  const name = str(x.name, 40);
  if (!name) throw new ClubError('The club needs a name');
  const squadsIn = x.squads && typeof x.squads === 'object' ? Object.entries(x.squads as Record<string, any>) : [];
  if (squadsIn.length > LIMITS.squads) throw new ClubError(`Up to ${LIMITS.squads} teams`);
  const squads: Record<string, Squad> = {};
  const seen = new Set<string>();
  for (const [key, sq] of squadsIn) {
    const players = Array.isArray(sq?.players) ? sq.players : [];
    if (players.length > LIMITS.players) throw new ClubError(`Up to ${LIMITS.players} players in a squad`);
    squads[slug(key, `squad-${Object.keys(squads).length + 1}`)] = {
      label: str(sq?.label, 30) || 'Squad',
      players: players.map((p: any, i: number) => {
        let id = slug(p?.id, `p${i + 1}`);
        while (seen.has(id)) id = `${id}-${i}`;
        seen.add(id);
        return { id, name: str(p?.name, 40), number: str(p?.number, 3).replace(/[^0-9]/g, ''), photo: assetPath(p?.photo), showPhoto: p?.showPhoto !== false };
      }),
    };
  }
  const sponsors = Array.isArray(x.sponsors) ? x.sponsors : [];
  if (sponsors.length > LIMITS.sponsors) throw new ClubError(`Up to ${LIMITS.sponsors} sponsors`);
  const league = Array.isArray(x.league) ? x.league : [];
  if (league.length > LIMITS.league) throw new ClubError(`Up to ${LIMITS.league} league opponents`);
  const videos = Array.isArray(x.videos) ? x.videos : [];
  if (videos.length > LIMITS.videos) throw new ClubError(`Up to ${LIMITS.videos} videos`);
  const w = Math.round(Number(x.screen?.width)), h = Math.round(Number(x.screen?.height));
  return {
    id: venue, name,
    sport: typeof x.sport === 'string' && RULES[x.sport] ? x.sport : 'rugby-union',
    teamName: str(x.teamName, 24) || name,
    short: str(x.short, 3).replace(/[^A-Za-z]/g, '').toUpperCase() || name.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase(),
    color: color(x.color, '#f5b400'),
    crest: assetPath(x.crest),
    screen: { width: w >= 64 && w <= 7680 ? w : 720, height: h >= 32 && h <= 4320 ? h : 480 },
    squads,
    sponsors: sponsors.map((s: any) => ({ name: str(s?.name, 40) || 'Sponsor', logo: assetPath(s?.logo), bg: color(s?.bg, '#ffffff') })).filter((s: Sponsor) => s.logo),
    league: league.map(cleanOpponent).filter(o => o.name),
    videos: videos.map((v: any, i: number) => {
      const d = Number(v?.durationMs);
      return { id: slug(v?.id, `v${i + 1}`), title: str(v?.title, 60) || 'Video', url: /\.(mp4|webm)$/.test(assetPath(v?.url)) ? assetPath(v?.url) : '',
        durationMs: Number.isFinite(d) && d > 0 && d < 3_600_000 ? Math.round(d) : null, bytes: Math.max(0, Math.round(Number(v?.bytes) || 0)) };
    }).filter((v: PromoVideo) => v.url),
  };
}

export function cleanOpponent(t: unknown): Opponent {
  const x = (t ?? {}) as Record<string, unknown>;
  const name = str(x.name, 24);
  const given = str(x.short, 3).replace(/[^A-Za-z]/g, '').toUpperCase();
  return { name, short: given || shortName(name), color: color(x.color), crest: assetPath(x.crest) || null };
}
